import { Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { usePublishProducer, useUnpublishProducer } from '@/hooks/usePublishProducer';

interface ProducerHeaderStatusProps {
  farmId: string;
}

/**
 * Inline status indicator + publish/unpublish action for the topbar.
 * Calm, editorial — no toasts, no alerts.
 */
export function ProducerHeaderStatus({ farmId }: ProducerHeaderStatusProps) {
  const { meta, hasCapability, isLoading } = useProducerCapabilities(farmId);
  const publishMutation = usePublishProducer();
  const unpublishMutation = useUnpublishProducer();

  if (isLoading || !meta) return null;
  if (meta.isSuspended) return null;

  const isPublished = meta.isPublic;
  const canPublish = hasCapability('publish.producer');

  return (
    <div className="flex items-center gap-2 shrink-0">
      {/* Status dot + label */}
      <div className="flex items-center gap-1.5">
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
            isPublished
              ? 'bg-emerald-500 dark:bg-emerald-400'
              : 'bg-muted-foreground/40'
          }`}
          aria-hidden="true"
        />
        <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">
          {isPublished ? 'Synlig i Farmly' : 'Inte publicerad ännu'}
        </span>
      </div>

      {/* Action button — icon on mobile, text on md+ */}
      {isPublished ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hidden md:inline-flex"
            disabled={unpublishMutation.isPending}
            onClick={() => unpublishMutation.mutate(farmId)}
          >
            {unpublishMutation.isPending && (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            )}
            Avpublicera
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 md:hidden"
            disabled={unpublishMutation.isPending}
            onClick={() => unpublishMutation.mutate(farmId)}
            aria-label="Avpublicera"
          >
            {unpublishMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <EyeOff className="h-3.5 w-3.5" />
            )}
          </Button>
        </>
      ) : canPublish ? (
        <>
          <Button
            size="sm"
            className="h-7 text-xs hidden md:inline-flex"
            disabled={publishMutation.isPending}
            onClick={() => publishMutation.mutate(farmId)}
          >
            {publishMutation.isPending && (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            )}
            Publicera
          </Button>
          <Button
            size="icon"
            className="h-7 w-7 md:hidden"
            disabled={publishMutation.isPending}
            onClick={() => publishMutation.mutate(farmId)}
            aria-label="Publicera"
          >
            {publishMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Eye className="h-3.5 w-3.5" />
            )}
          </Button>
        </>
      ) : null}
    </div>
  );
}
